import { useContext, useEffect, useId, useRef, useState } from 'react'
import clsx from 'clsx'
import { useCatalogEvent } from 'lib/analytics/catalog'
import { OwnerApiError, ownerApi, sharedMe } from 'lib/owner-notes/api'
import { authStateParam } from 'lib/owner-notes/analytics'
import {
  useOwnerCredentialOptional,
  useOwnerSessionOptional,
  type OwnerCredential,
} from 'lib/owner-notes/auth-bridge'
import { markOfferShown, useClaimAfterSignIn } from 'lib/owner-notes/claim-after-sign-in'
import { hasDeviceMarker, useDeviceEpoch } from 'lib/owner-notes/declared'
import { sendAsDevice } from 'lib/owner-notes/device'
import { TurnstileError, turnstileConfigured, useTurnstile } from 'lib/owner-notes/turnstile'
import {
  VOTE_QUESTION,
  helpfulAnswer,
  helpfulErrorMessage,
  helpfulOwn,
  helpfulRead,
  helpfulRollback,
  helpfulSeed,
  helpfulVoterChanged,
  isOwnReview,
  voteBreakdown,
  votePhrase,
  votePress,
  votePressed,
  type HelpfulState,
} from 'lib/community/helpful'
import {
  FEED_CONTROL_ANALYTICS,
  type CommunityHelpfulAnswer,
  type CommunityVoteCounts,
  type CommunityVoteValue,
  type FeedControlAnalytics,
} from 'lib/community/types'
import { MemberViewContext } from './useOwnMemberMe'
import styles from './VoteControl.module.css'

interface VoteControlProps {
  /** The review's note id. */
  noteId: string
  /** The review's author handle — the own-review check. */
  handle: string
  /** DealHunt's public Yes / No counts — what the server HTML prints (`voteCounts`). */
  counts: CommunityVoteCounts
  /** The reader's vote from the list's batched read (`useHelpfulMine`), once it lands. */
  known?: CommunityHelpfulAnswer
  /** The list's memory of DealHunt's answers, by note id: a remounted row keeps the vote. */
  memory?: Map<string, CommunityHelpfulAnswer>
  /** The reviewed product — the analytics target. */
  product: { node_id: string; name: string }
  className?: string
  /** The line's right end — the feed row's "Ask <handle>" (`AskButton`, feed plan slice 4). */
  end?: React.ReactNode
  /** The vote event's `page_type` / `location` — the feed's by default (a best list's Owners
   *  block passes its own through `FeedRow`). */
  analytics?: FeedControlAnalytics
}

/** DealHunt's "this is your own review" (either value, contract #202). */
const OWN_CODES: ReadonlySet<string> = new Set(['own_note'])

/** A bearer that did not verify: the reader signs in again (the vote is not replayed). */
const SIGN_IN_CODES: ReadonlySet<string> = new Set(['auth_required', 'auth_invalid'])

/** DealHunt before its db:push answers 503 `helpful_unavailable`, possibly without a sentence. */
const UNAVAILABLE_LINE = "Voting isn't available right now — try again later."

/** The claim after the toast's sign-in failed in a way a later sign-in may fix. */
const CLAIM_FAILED_LINE =
  "You're signed in, but we couldn't move your votes to your account yet — they still count."

const SIDES: ReadonlyArray<{ value: CommunityVoteValue; text: string }> = [
  { value: 'yes', text: 'Yes' },
  { value: 'no', text: 'No' },
]

/**
 * "Did this help you decide?" Yes / No on a community feed row (feed plan §2–§3, slice 2;
 * DealHunt contract #199–#202): a segmented control (ink on white, the chosen side filled, never
 * thumbs) and one public phrase — "13 of 16 found this helpful" from three votes, "Be the first to
 * say" under three. The author sees the full split ("13 yes · 3 no") on their own card only, and
 * the control is inert there (`isOwnReview`, as Helpful).
 *
 * Shared cache: the server HTML carries the public counts and the control in its UNKNOWN state —
 * neither side pressed, no toast, no sign-in branch. The reader's own vote arrives after
 * hydration, only from DealHunt's keyed answers (the list's batched `helpful/me` passed as
 * `known`, or a vote's answer); until then a tap changes no count (`votePress`).
 *
 * Who votes: a signed-in reader as the member; a signed-out reader with the owner-notes device —
 * a first vote on a browser without one mints it (`ensureDevice`, this control's own Turnstile,
 * rendered beside the row only when Cloudflare needs an interaction), and a device DealHunt
 * forgot is minted again once (`device_unknown`). After a signed-out vote counts: the toast
 * "Counted. Sign in to keep your votes." with the site's sign-in. Signing in links nothing by
 * itself (#81) — but the toast's "Sign in" is the reader's consent click: once its session arrives
 * the device is claimed (`useClaimAfterSignIn`, the Ask's arm-then-claim), and DealHunt folds the
 * device's votes into the member at the claim (5670520d) — a later Logout, which forgets the
 * device (#114), no longer takes them away.
 *
 * Pressed at once (2026-10-18): a tap shows its side pressed while the vote is in flight (the
 * device mint and the PUT can take seconds) — `votePressed`, dimmed while `busy`; a failure rolls
 * the look back with the error line. The figures still wait for DealHunt (876f5159).
 *
 * The voter changes under the control (sign-in, account switch, Logout): `helpfulVoterChanged` —
 * signed in, the next keyed read under the new bearer replaces what the control showed; signed
 * out, the reader's vote leaves the screen.
 */
const VoteControl: React.VFC<VoteControlProps> = ({
  noteId,
  handle,
  counts,
  known,
  memory,
  product,
  className,
  end,
  analytics = FEED_CONTROL_ANALYTICS,
}) => {
  const session = useOwnerSessionOptional()
  const { asVisitor } = useContext(MemberViewContext)
  const credential = useOwnerCredentialOptional()
  const epoch = useDeviceEpoch()
  const turnstile = useTurnstile()
  const { track } = useCatalogEvent()
  const labelId = useId()
  const [state, setState] = useState<HelpfulState>(() =>
    helpfulSeed(counts.yes, memory?.get(noteId), counts.no)
  )
  const [error, setError] = useState<string | null>(null)
  const [toast, setToast] = useState(false)
  const claim = useClaimAfterSignIn(credential, outcome => {
    if (outcome.kind === 'conflict') {
      setError(outcome.message)
    } else if (outcome.kind === 'failed') {
      setError(outcome.retry === 'none' ? outcome.lead : CLAIM_FAILED_LINE)
    }
  })
  // The latest state for the tap handler: two quick taps must see the first one's `busy`.
  const current = useRef(state)
  current.current = state

  useEffect(() => {
    if (!hasDeviceMarker()) {
      return undefined
    }
    let live = true
    sharedMe().then(
      me => {
        if (live) {
          setState(s => helpfulOwn(s, isOwnReview(me, { noteId, handle })))
        }
      },
      () => undefined
    )
    return () => {
      live = false
    }
  }, [epoch, handle, noteId])

  useEffect(() => {
    if (known) {
      setState(s => helpfulRead(s, known))
    }
  }, [known])

  // The voter changed (a new session token, or none): what the control knew was the previous
  // voter's. Not on mount — the seed and the batched read are already this voter's.
  const token = credential?.token ?? null
  const tokenRef = useRef(token)
  useEffect(() => {
    if (tokenRef.current === token) {
      return
    }
    tokenRef.current = token
    memory?.delete(noteId)
    setState(s => helpfulVoterChanged(s, token !== null))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- a new token is a new voter
  }, [token])

  // The toast offers the sign-in: on this page only its click runs the claim (not a carried one).
  useEffect(() => {
    if (toast) {
      markOfferShown()
    }
  }, [toast])

  // Signed in since the toast showed: it has said what it had to say.
  const signedIn = session.resolved && session.signedIn
  useEffect(() => {
    if (signedIn) {
      setToast(false)
    }
  }, [signedIn])

  const send = async (
    cred: OwnerCredential | null,
    value: CommunityVoteValue | null
  ): Promise<unknown> => {
    if (cred) {
      return ownerApi.vote(cred, noteId, value)
    }
    return sendAsDevice(turnstile.getToken, () => ownerApi.vote(null, noteId, value))
  }

  const press = async (choice: CommunityVoteValue): Promise<void> => {
    const before = current.current
    // Before the session check answers, a signed-in reader would vote as the device.
    if (!session.resolved || before.own || before.busy) {
      return
    }
    const cred = credential
    // Production DealHunt refuses a device minted without a bot check: without a site key a
    // signed-out reader with no device can only vote signed in.
    if (!cred && !hasDeviceMarker() && !turnstileConfigured()) {
      void session.showLogIn()
      return
    }
    const step = votePress(before, choice)
    if (!step) {
      return
    }
    current.current = step.next
    setState(step.next)
    setError(null)
    try {
      const answer = await send(cred, step.value)
      const next = helpfulAnswer(step.next, answer)
      if (!next) {
        setState(s => helpfulRollback(before, s))
        setError(helpfulErrorMessage('', null))
        return
      }
      const counted = answer as CommunityHelpfulAnswer
      memory?.set(noteId, {
        helpful: next.count,
        mine: next.mine,
        value: next.value,
        vote_counts: { yes: next.count, no: next.no },
      })
      setState(s => helpfulAnswer(s, counted) ?? s)
      if (step.value) {
        track('community_page_owner_vote', {
          page_type: analytics.page_type,
          location: analytics.location,
          action: step.value,
          auth_state: authStateParam(session),
          target_product_id: product.node_id,
          target_product_name: product.name,
        })
      }
      setToast(!cred && step.value !== null)
    } catch (e) {
      const code = e instanceof OwnerApiError ? e.code : ''
      const own = OWN_CODES.has(code)
      setState(s => helpfulRollback(before, s, own))
      if (SIGN_IN_CODES.has(code)) {
        void session.showLogIn()
      } else if (e instanceof TurnstileError) {
        setError(e.message)
      } else if (!own) {
        const message = e instanceof OwnerApiError ? e.message : null
        setError(
          code === 'helpful_unavailable' && !message
            ? UNAVAILABLE_LINE
            : helpfulErrorMessage(code, message)
        )
      }
    }
  }

  // The member hub's "View as visitor": the author's own control draws as a visitor sees it (a
  // tap stays a no-op — `press` reads the real state). Outside a hub the context is off.
  const shown = asVisitor && state.own ? { ...state, own: false } : state
  const pressed = votePressed(shown)

  return (
    <div className={clsx(styles.vote, className)}>
      <span id={labelId} className={styles.question}>
        {VOTE_QUESTION}
      </span>
      <span
        className={clsx(styles.seg, { [styles.own]: shown.own, [styles.busy]: shown.busy })}
        role="group"
        aria-labelledby={labelId}
        aria-busy={shown.busy || undefined}
      >
        {SIDES.map(side => (
          <button
            key={side.value}
            type="button"
            className={clsx(styles.side, { [styles.on]: pressed === side.value })}
            aria-pressed={shown.own ? undefined : pressed === side.value}
            aria-disabled={shown.own || undefined}
            title={shown.own ? 'Your own review' : undefined}
            onClick={() => void press(side.value)}
          >
            {side.text}
          </button>
        ))}
      </span>
      <span className={styles.phrase}>{shown.own ? voteBreakdown(shown) : votePhrase(shown)}</span>
      {toast && (
        <span className={styles.toast} role="status">
          {'Counted. '}
          <button
            type="button"
            className={styles.toastAction}
            onClick={() => {
              // The reader's consent click (#81): the device is claimed once this sign-in lands.
              claim.arm()
              void session.showLogIn()
            }}
          >
            {'Sign in'}
          </button>
          {' to keep your votes.'}
        </span>
      )}
      {error && (
        <span className={styles.error} role="status">
          {error}
        </span>
      )}
      {/* An interactive bot check (a first signed-out vote) shows here, beside the row. */}
      <div ref={turnstile.containerRef} className={styles.check} />
      {end}
    </div>
  )
}

export default VoteControl
